import { useState, useEffect } from "react";
import { Button, Tag, Radio, Input, Select, Modal, DatePicker } from "antd";
import { X, ExternalLink } from "lucide-react";
import dayjs from "dayjs";

// Import component Tab Lịch hẹn vừa tạo
import AppointmentTab from "./AppointmentTab";
import ImageTab from "./ImageTab";
import ServiceAndCardTab from "./ServiceAndCardTab";
import TouchHistoryTab from "./TouchHistoryTab";
import NeedsTab from "./NeedsTab";
import QuotationAndDepositTab from "./QuotationAndDepositTab";

interface CustomerDetailDrawerProps {
    customer: any;
    onClose: () => void;
    onSelectCustomer?: (customer: any) => void;
}

export default function CustomerDetailDrawer({ customer, onClose, onSelectCustomer }: CustomerDetailDrawerProps) {
    const [activeTab, setActiveTab] = useState("cskh");
    
    // State quản lý chế độ hiển thị: "normal" | "expanded" (~70%) | "minimized" (thu nhỏ)
    const [viewMode, setViewMode] = useState<"normal" | "expanded" | "minimized">("normal");

    // State quản lý tính năng Đang gọi & Nhắn tin Zalo
    const [isCalling, setIsCalling] = useState(false);
    const [selectedCsResult, setSelectedCsResult] = useState<string>("");
    const [callNote, setCallNote] = useState<string>("");

    // State riêng cho khung tin nhắn Zalo
    const [zaloTemplate, setZaloTemplate] = useState<string>("");
    const [zaloMessage, setZaloMessage] = useState<string>("");
    const [selectedZaloCsResult, setSelectedZaloCsResult] = useState<string>("");

    // State quản lý Modal Gắn cờ
    const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
    const [selectedFlag, setSelectedFlag] = useState<string>("Khách VIP");
    const [flagReason, setFlagReason] = useState<string>("");

    // State quản lý Modal Tạo việc (Công việc CSKH)
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [taskType, setTaskType] = useState<string>("Biến chứng");
    const [taskDueDate, setTaskDueDate] = useState<any>(dayjs("2026-10-16 09:00"));
    const [taskAssignee, setTaskAssignee] = useState<string>("NV-07");
    const [taskNote, setTaskNote] = useState<string>("");

    // Tự động bung drawer nếu đang thu nhỏ mà bấm chọn khách mới
    useEffect(() => {
        if (customer && viewMode === "minimized") {
            setViewMode("normal");
        }
        setIsCalling(false); // Reset trạng thái gọi khi đổi khách hàng
    }, [customer]);

    if (!customer) return null;

    if (viewMode === "minimized") {
        return (
            <div className="fixed bottom-3 right-4 z-50 bg-[#1d4c7d] text-white px-4 py-2 rounded-lg shadow-2xl flex items-center gap-3 border border-[#3E91AB]">
                <span className="w-2.5 h-2.5 rounded-full inline-block bg-green-400"></span>
                <span className="font-semibold text-sm">{customer.name} · {customer.code}</span>
                <div className="flex items-center gap-2 pl-2 border-l border-blue-400">
                    <Button
                        size="small"
                        type="text"
                        onClick={() => setViewMode("normal")}
                        className="text-white hover:text-yellow-200 text-xs p-0 h-auto font-medium underline"
                    >
                        Mở lại
                    </Button>
                    <button onClick={onClose} className="hover:text-red-300 text-white ml-1">
                        <X size={16} />
                    </button>
                </div>
            </div>
        );
    }

    // Tối ưu Responsive cho iPad & Mobile
    const drawerWidthClass = viewMode === "expanded" 
        ? "fixed inset-0 xl:relative xl:w-[150vw] z-41 xl:z-29" 
        : "fixed inset-0 xl:relative w-full xl:w-[70vw] z-41 xl:z-29";

    // Danh sách tab mặc định
    const defaultTabs = [
        { key: "cskh", label: "CSKH và y khoa" },
        { key: "lich", label: "Lịch hẹn" },
        { key: "hinh", label: "Hình ảnh" },
        { key: "dichvu", label: "Dịch vụ và thẻ" },
        { key: "lichsu", label: "Lịch sử chạm" },
        { key: "nhucau", label: "Nhu cầu" },
        { key: "baogia", label: "Báo giá và cọc" },
    ];

    // Các tab tính năng đặc biệt (Cuộc gọi, Nhắn tin · Zalo)
    const specialTabs = [
        { key: "cuocgoi", label: "Cuộc gọi", isSpecial: true },
        { key: "nhantin", label: "Nhắn tin · Zalo", isSpecial: true },
    ];

    // Luôn hiển thị các tab đặc biệt nếu đang gọi hoặc đang mở tab Zalo
    const showSpecialTabs = isCalling || activeTab === "cuocgoi" || activeTab === "nhantin";
    const displayTabs = showSpecialTabs ? [...specialTabs, ...defaultTabs] : defaultTabs;

    return (
        <div className={`${drawerWidthClass} bg-white dark:bg-[#1f1f1f] border-l border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-2xl text-sm transition-all duration-300 xl:h-[calc(100vh-100px)] overflow-y-auto`}>
            
            {/* Thanh Tab Danh Sách Khách Hàng & Nút Điều Khiển Top */}
            <div className="dark:bg-[#141414] border-b border-gray-200 dark:border-gray-800 px-3 py-2 flex items-center justify-between gap-2 overflow-x-auto sticky top-[0] z-20 bg-white">
                
                {/* Hiển thị widget Đang gọi nổi bật phía trên khi bấm Gọi khách này */}
                {isCalling ? (
                    <div className="bg-[#1d4c7d] text-white px-3 py-1 rounded text-xs font-semibold flex items-center gap-2 shadow">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        <span>Đang gọi</span>
                        <span>{customer.name} · {customer.code} · 00:59</span>
                        <button 
                            onClick={() => {
                                setIsCalling(false);
                                setActiveTab("cskh");
                            }} 
                            className="hover:text-red-300 ml-2 font-bold cursor-pointer text-sm"
                        >
                            ×
                        </button>
                    </div>
                ) : (
                    <div className="text-xs text-gray-500 font-medium">Hồ sơ chi tiết</div>
                )}

                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={() => setViewMode("minimized")}
                        className="px-2 py-1 text-xs bg-white dark:bg-[#252525] border border-gray-300 dark:border-gray-700 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-100 font-medium hidden xl:inline-block"
                    >
                        Thu nhỏ
                    </button>

                    <button
                        onClick={() => setViewMode(viewMode === "expanded" ? "normal" : "expanded")}
                        className="hidden xl:inline-block px-2 py-1 text-xs bg-white dark:bg-[#252525] border border-gray-300 dark:border-gray-700 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-100 font-medium"
                    >
                        {viewMode === "expanded" ? "Thu gọn" : "Mở rộng"}
                    </button>

                    <button
                        onClick={onClose}
                        className="px-2.5 py-1 text-xs bg-red-500 text-white rounded font-bold hover:bg-red-600 transition-colors flex items-center gap-1"
                    >
                        <X size={14} /> Đóng
                    </button>
                </div>
            </div>

            {/* Khung DỮ LIỆU MINH HOẠ */}
            <div className="bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1 text-xs font-bold border-b border-orange-200 dark:border-orange-900 tracking-wider">
                DỮ LIỆU MINH HOẠ
            </div>

            {/* Nội Dung Chi Tiết Khách Hàng */}
            <div className="p-4 overflow-y-auto space-y-4 flex-1 dark:text-gray-200 pb-20 xl:pb-4">
                
                {/* Thông tin chính & Mã CS */}
                <div className="flex justify-between items-start gap-2">
                    <div>
                        <div className="text-base font-bold text-gray-900 dark:text-gray-100">
                            {customer.name} <span className="text-xs font-normal text-gray-500">· xưng hô: anh/chị</span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{customer.code}</span> · {customer.phone} · <span className="text-[#3E91AB] cursor-pointer font-medium hover:underline">Hiện số</span>
                        </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <Tag className="bg-[#1d4c7d] text-white font-bold border-0 m-0 text-xs px-2 py-0.5">
                            {customer.csCode || "CS2"}
                        </Tag>
                        <div className="text-[11px] text-gray-500 mt-1">Đang trong liệu trình<br />Ở mã này: 30 ngày</div>
                    </div>
                </div>

                {/* Bảng thuộc tính chi tiết */}
                <div className="bg-gray-50 dark:bg-[#252525] p-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
                    <div><span className="text-gray-500">Phòng phụ trách:</span> <span className="font-medium">CSKH (R22)</span></div>
                    <div><span className="text-gray-500">Mảng:</span> <span className="font-medium">{customer.serviceCategory || "Newhair"}</span></div>
                    <div><span className="text-gray-500">Kênh khách muốn:</span> <span className="font-medium">Gọi (Gọi / Zalo / WhatsApp / Chỉ nhắn tin)</span></div>
                    <div><span className="text-gray-500">Loại khách:</span> <span className="font-medium">Khách cũ · 2 lần dùng dịch vụ</span></div>
                    <div><span className="text-gray-500">Thẻ Member:</span> <span className="font-medium">Chưa có</span></div>
                    <div><span className="text-gray-500">Phụ trách:</span> <span className="font-medium">Telesales: NV-01 · CSKH: NV-07</span></div>
                    <div className="col-span-full"><span className="text-gray-500">Nguồn:</span> <span className="font-medium">Zalo · Khách giới thiệu</span></div>
                </div>

                {/* Các nhãn cờ / trạng thái */}
                <div className="flex flex-wrap gap-1.5 items-center">
                    <Tag color="red" className="m-0 font-medium">Khiếu nại đang mở</Tag>
                    <Tag color="orange" className="m-0 font-medium">Còn nợ</Tag>
                </div>

                {/* Thư viện ảnh thu nhỏ */}
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 overflow-x-auto py-1">
                        {[
                            { label: "Đỉnh" },
                            { label: "Trước" },
                            { label: "Đỉnh" },
                            { label: "2 bên" },
                        ].map((item, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-1 flex-shrink-0">
                                <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs text-gray-500 cursor-pointer hover:border-[#3E91AB]">
                                    Ảnh
                                </div>
                                <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                                    {item.label}
                                </span>
                            </div>
                        ))}
                        <div className="flex items-center text-xs text-[#3E91AB] font-medium cursor-pointer gap-1 pl-2 whitespace-nowrap hover:underline">
                            Mở tab Hình ảnh <ExternalLink size={13} />
                        </div>
                    </div>
                </div>

                {/* Các nút thao tác nhanh */}
                <div className="flex flex-wrap gap-2 pt-1">
                    <Button 
                        type="primary" 
                        onClick={() => {
                            setIsCalling(true);
                            setActiveTab("cuocgoi");
                        }} 
                        className="!bg-[#1d4c7d] hover:!bg-[#153a60] font-medium text-xs h-8"
                    >
                        Gọi khách này
                    </Button>
                    
                    <Button 
                        onClick={() => setActiveTab("nhantin")}
                        className="font-medium text-xs h-8 border-gray-300 dark:border-gray-600"
                    >
                        Zalo
                    </Button>
                </div>

                {/* Các nút thao tác nhanh phụ */}
                <div className="flex flex-wrap gap-2">
                    <Button danger className="font-medium text-xs h-8">Báo biến chứng</Button>
                    <Button className="font-medium text-xs h-8">Tạo phiếu khiếu nại</Button>

                    <Button 
                        onClick={() => setIsFlagModalOpen(true)}
                        className="font-medium text-xs h-8"
                    >
                        Gắn cờ
                    </Button>

                    <Button 
                        onClick={() => setIsTaskModalOpen(true)}
                        className="font-medium text-xs h-8"
                    >
                        + Tạo việc
                    </Button>
                </div>

                {/* Khung gợi ý việc nên làm */}
                <div className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-950/30 p-2.5 rounded-r text-xs space-y-1 text-orange-900 dark:text-orange-200">
                    <div className="font-bold flex items-center justify-between">
                        <span>Gợi ý việc nên làm <span className="bg-amber-600 text-white text-[10px] px-1.5 py-0.2 rounded cursor-pointer ml-1">[?] bộ quy tắc gợi ý</span></span>
                    </div>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 dark:text-gray-300">
                        <li>Phiếu KN-0412 đã quá 1 ngày → gọi xác nhận hướng giải quyết, báo Quản lý CSKH.</li>
                        <li>Còn nợ 5.000.000 đ → không nhắc nợ trong cuộc gọi khiếu nại; chuyển Kế toán.</li>
                        <li>Ngoài thẩm quyền CSKH (voucher trên 500K, hoàn tiền) → leo thang theo SOP.</li>
                    </ul>
                </div>

                {/* Thanh Tab chung */}
                <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2 sm:gap-3 text-xs font-semibold overflow-x-auto pt-1">
                    {displayTabs.map((tab) => {
                        const isActive = activeTab === tab.key;
                        
                        if (tab.isSpecial) {
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`px-3 py-1.5 font-bold transition-all whitespace-nowrap rounded-t ${
                                        isActive
                                            ? "bg-[#d97706] text-white shadow-sm"
                                            : "bg-orange-100 dark:bg-orange-950/60 text-orange-800 dark:text-orange-300 hover:bg-orange-200"
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            );
                        }

                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`pb-2 px-1 whitespace-nowrap transition-colors ${
                                    isActive
                                        ? "text-[#1d4c7d] dark:text-blue-400 border-b-2 border-[#1d4c7d] dark:border-blue-400 font-bold"
                                        : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* HIỂN THỊ NỘI DUNG TỪNG TAB */}

                {/* TAB 1: Cuộc gọi */}
                {activeTab === "cuocgoi" && (
                    <div className="space-y-3 pt-2 animate-fade-in">
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-3 text-xs space-y-2">
                            <div className="font-bold text-[#1d4c7d] dark:text-blue-400 flex items-center justify-between">
                                <span>▼ Hướng dẫn cuộc gọi CSKH (SOP CSKH)</span>
                                <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.5 rounded font-bold">[?] bản chính thức</span>
                            </div>
                            <div className="space-y-1.5 text-gray-700 dark:text-gray-300 leading-relaxed">
                                <p><strong>T+24h / theo dõi sau dịch vụ:</strong> hỏi thăm tình trạng sau buổi làm, có gì bất thường không. Khách nêu đau, sưng, đỏ bất thường → chọn CS-03, kết nối bác sĩ ngay.</p>
                                <p><strong>Khiếu nại:</strong> Tiếp nhận → Xác minh → Giải quyết trong thẩm quyền (voucher ≤ 500K, đổi lịch) → hỏi lại sau 24–48 giờ → ghi nhận. Vượt thẩm quyền → leo thang.</p>
                                <p><strong>Nhắc lịch / thẻ / gọi lại khách cũ:</strong> nhắc buổi còn lại theo lịch bác sĩ chỉ định; khách muốn thêm dịch vụ → CS-06.</p>
                                <p className="text-red-600 font-semibold"><strong>KHÔNG LÀM:</strong> không tự tư vấn y tế · không tranh luận · không hứa kết quả · không giảm giá theo %.</p>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-[#252525] p-3 rounded border border-gray-200 dark:border-gray-700 space-y-3 text-xs">
                            <div className="font-bold text-gray-900 dark:text-gray-100 flex items-center justify-between">
                                <span>Kết quả cuộc gọi — bắt buộc trước khi đóng (R01)</span>
                                <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.5 rounded cursor-pointer font-bold">[?] danh sách mã</span>
                            </div>
                            
                            <div className="font-bold text-gray-800 dark:text-gray-200">Bộ mã CSKH</div>
                            
                            <Radio.Group onChange={(e) => setSelectedCsResult(e.target.value)} value={selectedCsResult} className="space-y-2 flex flex-col">
                                <Radio value="CS-01">CS-01 Ổn định</Radio>
                                <Radio value="CS-02">CS-02 Phản ứng nhẹ, hẹn theo dõi</Radio>
                                <Radio value="CS-03">CS-03 Biến chứng → chuyển bác sĩ</Radio>
                                <Radio value="CS-04">CS-04 Khiếu nại → tạo phiếu</Radio>
                                <Radio value="CS-05">CS-05 Đặt lịch buổi tiếp / tái khám</Radio>
                                <Radio value="CS-06">CS-06 Muốn thêm dịch vụ</Radio>
                                <Radio value="CS-07">CS-07 Không liên lạc được</Radio>
                                <Radio value="CS-08">CS-08 Khách từ chối nhận cuộc gọi → cờ Không gọi (R19)</Radio>
                            </Radio.Group>

                            <div className="space-y-1 pt-1">
                                <div className="font-bold text-gray-800 dark:text-gray-200">Ghi chú</div>
                                <Input.TextArea 
                                    rows={2} 
                                    placeholder="Ngắn gọn; không ghi chi tiết bệnh lý"
                                    value={callNote}
                                    onChange={(e) => setCallNote(e.target.value)}
                                    className="text-xs"
                                />
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <Button 
                                    type="primary" 
                                    onClick={() => {
                                        alert("Đã lưu kết quả cuộc gọi thành công!");
                                        setIsCalling(false);
                                        setActiveTab("cskh");
                                    }}
                                    className="!bg-[#1d4c7d] text-xs h-8 px-4 font-bold"
                                >
                                    Lưu kết quả
                                </Button>
                                <Button 
                                    onClick={() => {
                                        alert("Đã lưu và sẵn sàng gọi tiếp!");
                                        setSelectedCsResult("");
                                        setCallNote("");
                                    }}
                                    className="text-xs h-8 px-4 border-gray-300 font-medium"
                                >
                                    Lưu và gọi tiếp
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 2: Nhắn tin · Zalo */}
                {activeTab === "nhantin" && (
                    <div className="space-y-3 pt-2 text-xs animate-fade-in">
                        <div className="text-gray-600 dark:text-gray-300 leading-relaxed">
                            Kênh <strong>Zalo</strong> · tin 2 chiều tự động bộ vào hồ sơ và Lịch sử chạm (cách kết nối do BUSO quyết) · khách trả lời → về đúng người phụ trách NV-07, không vào nhóm Trực page (R25) · chuẩn phản hồi ≤ 5 phút trong ca
                        </div>

                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-[#141414] space-y-3 max-h-60 overflow-y-auto">
                            <div className="bg-white dark:bg-[#252525] p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 max-w-[85%] space-y-1">
                                <div className="text-[11px] text-gray-500">Khách · 13/10 11:10</div>
                                <div className="text-gray-800 dark:text-gray-200">Hôm nay tái khám anh chờ gần 1 tiếng, hẹn 9h mà gần 10h mới vào</div>
                            </div>

                            <div className="bg-blue-50 dark:bg-blue-950/40 p-2.5 rounded-lg border border-blue-200 dark:border-blue-900 max-w-[85%] ml-auto space-y-1">
                                <div className="text-[11px] text-gray-500">Nhân viên · 13/10 11:30</div>
                                <div className="text-gray-800 dark:text-gray-200">Dạ em xin lỗi anh vì phải chờ lâu. Em ghi nhận và báo quản lý ngay ạ.</div>
                                <div className="text-[11px] text-blue-600 dark:text-blue-400 font-medium pt-0.5">Đã gửi → Đã nhận → Khách trả lời</div>
                            </div>

                            <div className="bg-white dark:bg-[#252525] p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 max-w-[85%] space-y-1">
                                <div className="text-[11px] text-gray-500">Khách · 13/10 11:35</div>
                                <div className="text-gray-800 dark:text-gray-200">Lần sau mà vậy chắc anh không quay lại</div>
                            </div>

                            <div className="bg-blue-50 dark:bg-blue-950/40 p-2.5 rounded-lg border border-blue-200 dark:border-blue-900 max-w-[85%] ml-auto space-y-1">
                                <div className="text-[11px] text-gray-500">Nhân viên · 14/10 10:00</div>
                                <div className="text-gray-800 dark:text-gray-200">Dạ em đã ghi nhận phản ánh của anh (phiếu KN-0412). Bên em xử lý và báo lại anh trước 16/10. Em xin lỗi vì trải nghiệm chưa tốt ạ.</div>
                                <div className="text-[11px] text-blue-600 dark:text-blue-400 font-medium pt-0.5">Đã gửi → Đã nhận → Khách trả lời</div>
                            </div>
                        </div>

                        <div className="space-y-2 pt-1">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-800 dark:text-gray-200 whitespace-nowrap">Mẫu tin</span>
                                <Select
                                    size="small"
                                    value={zaloTemplate || undefined}
                                    placeholder="— chọn mẫu (CSKH) —"
                                    onChange={(val) => setZaloTemplate(val)}
                                    className="w-full text-xs"
                                    options={[
                                        { label: "Mẫu 1: Xin lỗi chờ lâu & Ghi nhận khiếu nại", value: "mau1" },
                                        { label: "Mẫu 2: Nhắc lịch tái khám định kỳ", value: "mau2" },
                                        { label: "Mẫu 3: Hỏi thăm tình trạng T+24h sau làm", value: "mau3" },
                                    ]}
                                />
                            </div>

                            <Input.TextArea 
                                rows={3}
                                placeholder="Soạn tin; mẫu tự điền tên, dịch vụ, giá từ, hạn ưu đãi, ngày hẹn"
                                value={zaloMessage}
                                onChange={(e) => setZaloMessage(e.target.value)}
                                className="text-xs"
                            />

                            <div className="flex items-center justify-between pt-1">
                                <div className="flex items-center gap-2">
                                    <Button 
                                        type="primary"
                                        onClick={() => {
                                            alert("Đã gửi tin nhắn Zalo thành công!");
                                            setZaloMessage("");
                                        }}
                                        className="!bg-[#1d4c7d] text-xs h-8 px-4 font-bold"
                                    >
                                        Gửi
                                    </Button>
                                    <span className="text-gray-500 italic text-[11px]">Không nêu tên máy, không hứa kết quả.</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-[#252525] p-3 rounded border border-gray-200 dark:border-gray-700 space-y-3 mt-3">
                            <div className="font-bold text-[#1d4c7d] dark:text-blue-400 text-sm">
                                Mã kết quả lượt nhắn — bắt buộc (R24 · R01)
                            </div>
                            
                            <div className="font-bold text-gray-800 dark:text-gray-200">Bộ mã CSKH</div>

                            <Radio.Group onChange={(e) => setSelectedZaloCsResult(e.target.value)} value={selectedZaloCsResult} className="space-y-2 flex flex-col">
                                <Radio value="CS-01">CS-01 Ổn định</Radio>
                                <Radio value="CS-02">CS-02 Phản ứng nhẹ, hẹn theo dõi</Radio>
                                <Radio value="CS-03">CS-03 Biến chứng → chuyển bác sĩ</Radio>
                                <Radio value="CS-04">CS-04 Khiếu nại → tạo phiếu</Radio>
                                <Radio value="CS-05">CS-05 Đặt lịch buổi tiếp / tái khám</Radio>
                                <Radio value="CS-06">CS-06 Muốn thêm dịch vụ</Radio>
                                <Radio value="CS-07">CS-07 Không liên lạc được</Radio>
                                <Radio value="CS-08">CS-08 Khách từ chối nhận cuộc gọi → cờ Không gọi (R19)</Radio>
                            </Radio.Group>

                            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <Button 
                                    type="primary"
                                    onClick={() => alert("Đã lưu mã kết quả lượt nhắn Zalo!")}
                                    className="!bg-[#1d4c7d] text-xs h-8 px-4 font-bold"
                                >
                                    Lưu lượt nhắn
                                </Button>
                                <span className="text-gray-600 dark:text-gray-400 text-xs flex items-center gap-1">
                                    Tin gửi quá X giờ <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.5 rounded cursor-pointer font-bold">[?]</span> chưa có trả lời → tự tạo việc chạm lại (R24)
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 3: CSKH và y khoa */}
                {activeTab === "cskh" && (
                    <div className="space-y-2 text-xs animate-fade-in">
                        <div className="grid grid-cols-2 gap-2 py-1 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-500">CSAT</span>
                            <span className="font-medium">5 (22/09) · chưa trả lời (13/10)</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 py-1 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-500">Khiếu nại</span>
                            <span className="text-red-600 font-bold">KN-0412 · Dịch vụ — chờ lâu · <span className="underline">đang xử lý, quá hạn 1 ngày</span></span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 py-1 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-500">Phiếu chuyển bác sĩ</span>
                            <span className="font-medium">Không</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 py-1 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-500">Cờ cần bác sĩ xem trước</span>
                            <span className="font-medium">Không</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 py-1 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-500">Consent đã ký</span>
                            <span className="font-medium">Có · 15/09</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 py-1">
                            <span className="text-gray-500">Đồng ý dùng ảnh truyền thông</span>
                            <span className="font-medium">Không (mặc định)</span>
                        </div>
                    </div>
                )}

                {activeTab === "lich" && <AppointmentTab />}

                {activeTab === "hinh" && <ImageTab customerCode={customer.code} />}

                {activeTab === "dichvu" && <ServiceAndCardTab />}

                {activeTab === "lichsu" && <TouchHistoryTab />}

                {activeTab === "nhucau" && <NeedsTab />}

                {activeTab === "baogia" && <QuotationAndDepositTab />}

                <div className="text-[11px] text-gray-400 italic pt-2 border-t border-gray-100 dark:border-gray-800">
                    Chỉ thấy cờ và trạng thái phiếu, không thấy chi tiết bệnh lý. Chi tiết y khoa thuộc hồ sơ bệnh án. <span className="bg-amber-600 text-white text-[10px] px-1.5 py-0.2 rounded font-semibold cursor-pointer">[?] ranh giới CMS ⇄ HIS</span>
                </div>

            </div>

            {/* Modal Gắn cờ */}
            <Modal
                title={<div className="font-bold text-gray-900 text-base">Gắn cờ · {customer.code}</div>}
                open={isFlagModalOpen}
                onCancel={() => setIsFlagModalOpen(false)}
                footer={null}
                width={450}
                centered
            >
                <div className="space-y-4 pt-2 text-xs">
                    <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-700 w-12 flex-shrink-0">Cờ</span>
                        <Select
                            value={selectedFlag}
                            onChange={(val) => setSelectedFlag(val)}
                            className="w-full text-xs"
                            options={[
                                { label: "Khách VIP", value: "Khách VIP" },
                                { label: "Không gọi", value: "Không gọi" },
                                { label: "Ở nước ngoài", value: "Ở nước ngoài" },
                                { label: "Có thể trùng hồ sơ", value: "Có thể trùng hồ sơ" },
                                { label: "Biến chứng", value: "Biến chứng" },
                                { label: "Khiếu nại", value: "Khiếu nại" },
                            ]}
                        />
                    </div>

                    <div className="flex items-start gap-3">
                        <span className="font-medium text-gray-700 w-12 flex-shrink-0 pt-1">Lý do</span>
                        <Input.TextArea
                            rows={2}
                            value={flagReason}
                            onChange={(e) => setFlagReason(e.target.value)}
                            className="w-full text-xs"
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                        <Button 
                            onClick={() => setIsFlagModalOpen(false)}
                            className="text-xs h-8 px-4"
                        >
                            Huỷ
                        </Button>
                        <Button 
                            type="primary"
                            onClick={() => {
                                alert(`Đã gắn cờ "${selectedFlag}" cho khách hàng ${customer.code}`);
                                setIsFlagModalOpen(false);
                                setFlagReason("");
                            }}
                            className="!bg-[#1d4c7d] text-xs h-8 px-4 font-bold"
                        >
                            Gắn cờ
                        </Button>
                    </div>

                    <div className="text-[11px] text-gray-500 pt-2 border-t border-gray-100 leading-relaxed">
                        Cờ chạy song song với trạng thái, không đổi mã S / CS (R37). Gắn / gỡ cờ ghi nhật ký. Danh sách cờ <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded cursor-pointer font-bold">[?]</span>
                    </div>
                </div>
            </Modal>

            {/* Modal + Tạo việc (Công việc CSKH) */}
            <Modal
                title={<div className="font-bold text-gray-900 text-base">+ Tạo việc (Công việc CSKH)</div>}
                open={isTaskModalOpen}
                onCancel={() => setIsTaskModalOpen(false)}
                footer={null}
                width={480}
                centered
            >
                <div className="space-y-3.5 pt-2 text-xs">
                    <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-700 w-20 flex-shrink-0">Khách</span>
                        <Select
                            value={`${customer.code} · ${customer.name}`}
                            disabled
                            className="w-full text-xs"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-700 w-20 flex-shrink-0">Loại việc</span>
                        <Select
                            value={taskType}
                            onChange={(val) => setTaskType(val)}
                            className="w-full text-xs"
                            options={[
                                { label: "Biến chứng", value: "Biến chứng" },
                                { label: "Khiếu nại", value: "Khiếu nại" },
                                { label: "Gọi lại khách cũ", value: "Gọi lại khách cũ" },
                                { label: "Đặt lịch tái khám", value: "Đặt lịch tái khám" },
                                { label: "Nhắc thanh toán", value: "Nhắc thanh toán" },
                            ]}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-700 w-20 flex-shrink-0">Hạn</span>
                        <DatePicker 
                            showTime
                            format="MM/DD/YYYY hh:mm A"
                            value={taskDueDate}
                            onChange={(date) => setTaskDueDate(date)}
                            className="w-full text-xs"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-700 w-20 flex-shrink-0">Người nhận</span>
                        <Select
                            value={taskAssignee}
                            onChange={(val) => setTaskAssignee(val)}
                            className="w-full text-xs"
                            options={[
                                { label: "NV-07", value: "NV-07" },
                                { label: "NV-01", value: "NV-01" },
                                { label: "NV-02", value: "NV-02" },
                                { label: "Quản lý CSKH", value: "Quản lý CSKH" },
                            ]}
                        />
                    </div>

                    <div className="flex items-start gap-3">
                        <span className="font-medium text-gray-700 w-20 flex-shrink-0 pt-1">Ghi chú</span>
                        <Input.TextArea
                            rows={2}
                            placeholder="Không ghi chi tiết bệnh lý"
                            value={taskNote}
                            onChange={(e) => setTaskNote(e.target.value)}
                            className="w-full text-xs"
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button 
                            onClick={() => setIsTaskModalOpen(false)}
                            className="text-xs h-8 px-4"
                        >
                            Huỷ
                        </Button>
                        <Button 
                            type="primary"
                            onClick={() => {
                                alert(`Đã tạo công việc "${taskType}" thành công cho ${customer.code}!`);
                                setIsTaskModalOpen(false);
                                setTaskNote("");
                            }}
                            className="!bg-[#1d4c7d] text-xs h-8 px-4 font-bold"
                        >
                            Tạo việc
                        </Button>
                    </div>

                    <div className="text-[11px] text-gray-500 pt-2 border-t border-gray-100 leading-relaxed">
                        Việc tạo tay ghi nguồn "Tạo tay · người tạo" và một dòng Lịch sử chạm.<br />
                        Biến chứng / Khiếu nại tạo tay cũng gắn cờ lên hồ sơ.
                    </div>
                </div>
            </Modal>

        </div>
    );
}